import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Trash2, UserPlus, Disc3, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-white/5 border-white/10 h-11 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

export default function DjsPanel({ token }) {
  const [djs, setDjs] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "dj" });
  const [saving, setSaving] = useState(false);

  const auth = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/djs`, { headers: auth() });
      setDjs(data);
    } catch {
      toast.error("Could not load DJs.");
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/djs`, form, { headers: auth() });
      toast.success(
        `${form.name} can now sign in at ${form.role === "admin" ? "/admin" : "/dj"}`
      );
      setForm({ name: "", email: "", password: "", role: "dj" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add DJ.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}'s access?`)) return;
    try {
      await axios.delete(`${API}/djs/${id}`, { headers: auth() });
      toast.success("DJ removed.");
      load();
    } catch {
      toast.error("Could not remove DJ.");
    }
  };

  return (
    <section data-testid="djs-panel" className="space-y-6">
      <form
        data-testid="dj-add-form"
        onSubmit={add}
        className="glass rounded-3xl p-6 sm:p-8 space-y-5"
      >
        <Overline>Add Staff</Overline>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="dj-name">Name</Label>
            <Input
              id="dj-name"
              data-testid="dj-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="DJ Rick"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dj-email-add">Email</Label>
            <Input
              id="dj-email-add"
              type="email"
              data-testid="dj-add-email-input"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="dj@dreamentertainment.org"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dj-pass">Temporary Password</Label>
            <Input
              id="dj-pass"
              data-testid="dj-password-set-input"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Min. 8 characters"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label>Access Level</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger data-testid="staff-role-select" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dj" data-testid="staff-role-dj">
                  DJ (live dashboard only)
                </SelectItem>
                <SelectItem value="admin" data-testid="staff-role-admin">
                  Full Admin (everything)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <button
          type="submit"
          data-testid="dj-add-btn"
          disabled={saving}
          className="rounded-full bg-primary text-primary-foreground px-8 py-3 text-xs font-medium tracking-[0.2em] uppercase transition-[transform,opacity] duration-300 hover:scale-105 disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          Add Staff
        </button>
      </form>

      {djs === null ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      ) : djs.length === 0 ? (
        <p data-testid="djs-empty" className="text-center text-muted-foreground py-16 font-serif text-2xl">
          No staff accounts yet.
        </p>
      ) : (
        <div className="space-y-3" data-testid="djs-list">
          {djs.map((d) => (
            <article
              key={d.id}
              data-testid={`dj-row-${d.id}`}
              className="glass rounded-2xl p-5 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center text-primary">
                  {d.role === "admin" ? <ShieldCheck size={18} /> : <Disc3 size={18} />}
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {d.name}
                    <span className={`ml-2 text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${
                      d.role === "admin"
                        ? "border-primary/50 text-primary"
                        : "border-white/15 text-muted-foreground"
                    }`}>
                      {d.role === "admin" ? "Full Admin" : "DJ"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">{d.email}</p>
                </div>
              </div>
              <button
                data-testid={`dj-remove-${d.id}`}
                onClick={() => remove(d.id, d.name)}
                aria-label={`Remove ${d.name}`}
                className="text-muted-foreground/60 hover:text-accent transition-colors duration-300 p-1"
              >
                <Trash2 size={16} />
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
